"use client";
import { IsolationHistory } from "@/app/(application)/(normal)/isolate/components/output/IsolationHistory";
import { NoIsolations } from "@/app/(application)/(normal)/isolate/components/output/NoIsolations";
import { AudioPlayerDownloadIsolation } from "@/components/audioPlayers/audioPlayerDownloadIsolation/AudioPlayerDownloadIsolation";
import { AudioOutputSkeleton } from "@/components/skeletons/audio/AudioOutputSkeleton";
import { Heading4 } from "@/components/text/Heading4";
import { SoftText } from "@/components/text/SoftText";
import {
  MAX_NUMBER_OF_ISOLATIONS_HISTORY,
  SECONDS_CHECK_IF_ISOLATION_UPDATE,
} from "@/constants/constants";
import { checkIsolationsPercentagesDoneAction } from "@/data/isolation/actions/checkIsolationsPercentagesDoneAction";
import { IsolationConstructedNoDates, UserNoDates } from "@/types/types";
import { useStatusPolling } from "@/utils/hooks/useStatusPolling";

const isAnyIsolationRunning = (isolations: IsolationConstructedNoDates[]) =>
  isolations.some(
    (isolation) => isolation.confirmedDone !== true && !isolation.failed
  );

const checkIsolationsStatus = (isolations: IsolationConstructedNoDates[]) =>
  checkIsolationsPercentagesDoneAction({
    isolationsInput: isolations.map((isolation) => ({
      id: isolation.id,
      percentageDone: isolation.percentageDone,
      confirmedDone: isolation.confirmedDone,
      failed: isolation.failed,
    })),
  });

export function Output({
  isolations,
  user,
  shouldShowLoadState,
}: {
  isolations: IsolationConstructedNoDates[] | null;
  user: UserNoDates | null;
  shouldShowLoadState: boolean;
}) {
  const hasUser = user !== null;
  const hasIsolations = isolations !== null && isolations.length !== 0;
  // If there are isolations that are running setup polling
  useStatusPolling({
    pollInput: isolations,
    isRunning: isAnyIsolationRunning,
    checkStatus: checkIsolationsStatus,
    baseDelayMs: SECONDS_CHECK_IF_ISOLATION_UPDATE * 1000,
    statusLabel: "isolations",
  });

  return (
    <div>
      <div className="rounded-3xl bg-gray-50 px-4 py-4 shadow-sm ring-1 ring-gray-900/5 sm:px-8 sm:py-8">
        <Heading4 elementType="h2">Output</Heading4>
        <SoftText elementType="p" className="mb-4 mt-1">
          Your last {MAX_NUMBER_OF_ISOLATIONS_HISTORY} isolations are saved.
          Make sure to download any isolations that you want to keep.
        </SoftText>

        {(!hasUser || !hasIsolations) && !shouldShowLoadState && (
          <NoIsolations />
        )}
        {hasUser && hasIsolations && !shouldShowLoadState && (
          <>
            <div>
              <AudioPlayerDownloadIsolation
                isolation={isolations[0]}
                user={user}
              />
            </div>
            {isolations.length > 1 && (
              <IsolationHistory
                isolationHistory={isolations.slice(1)}
                user={user}
              />
            )}
          </>
        )}
        {shouldShowLoadState && <AudioOutputSkeleton size="medium" />}
      </div>
    </div>
  );
}
